/**
 * CT Counter — animated number count-up, dependency-free.
 *
 * Finds numbers inside any `.ct-animate-numbers` element (standalone or mid
 * sentence) and counts them up from 0 when scrolled into view. Self-contained:
 * IntersectionObserver + requestAnimationFrame, no GSAP.
 *
 * Anti-jank: each number is wrapped so an invisible in-flow "sizer" (the final
 * value) reserves the width and provides the baseline, while the animating
 * digits overlay it absolutely — the box never reflows and the line never
 * jumps. tabular-nums keeps every digit the same width. Honors
 * prefers-reduced-motion (jumps to final) and is accessible (the sizer carries
 * the real value; the animating digits are aria-hidden).
 */

const NUMBER_RE = /\d[\d,]*(?:\.\d+)?/;
const NUMBER_RE_G = new RegExp('(' + NUMBER_RE.source + ')', 'g');
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Format a number with a fixed number of decimals and optional thousands commas.
 * @param {number} value
 * @param {number} decimals
 * @param {boolean} useCommas
 * @returns {string}
 */
function formatNumber(value, decimals, useCommas) {
  const fixed = value.toFixed(decimals);
  if (!useCommas) return fixed;
  const [intPart, decPart] = fixed.split('.');
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart != null ? withCommas + '.' + decPart : withCommas;
}

class CtNumberCounter extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ctReady) return;
    this.dataset.ctReady = 'true';

    this.endValue = parseFloat((this.dataset.endValue || '0').replace(/,/g, '')) || 0;
    this.useCommas = (this.dataset.format || '').includes(',');
    const decPart = (this.dataset.format || '').split('.')[1];
    this.decimals = decPart ? decPart.length : 0;

    this.valueEl = this.querySelector('.ct-counter__value');
    if (!this.valueEl) return;

    if (reduceMotion()) {
      this.finish();
      return;
    }

    this.valueEl.textContent = formatNumber(0, this.decimals, this.useCommas);

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.observer = null;
        this.animate();
      },
      // Fire once the element passes ~80% down the viewport, matching the
      // original ScrollTrigger 'top 80%' feel.
      { rootMargin: '0px 0px -20% 0px', threshold: 0 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  finish() {
    if (this.valueEl) this.valueEl.textContent = this.dataset.format || String(this.endValue);
  }

  animate() {
    const end = this.endValue;
    // Duration scales gently with magnitude (≈0.8s for small, capped at 2s).
    const duration = Math.min(2000, 800 + Math.log10(Math.max(1, Math.abs(end))) * 300);
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const current = end * easeOutCubic(progress);
      this.valueEl.textContent = formatNumber(current, this.decimals, this.useCommas);
      if (progress < 1) {
        this.raf = requestAnimationFrame(step);
      } else {
        this.finish();
      }
    };
    this.raf = requestAnimationFrame(step);
  }
}

if (!customElements.get('ct-number-counter')) {
  customElements.define('ct-number-counter', CtNumberCounter);
}

/**
 * Replace each number in an element's text nodes with a counter structure.
 * @param {Element} el
 */
function replaceNumbersInElement(el) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  for (const node of textNodes) {
    const text = node.nodeValue;
    if (!text || !/\d/.test(text)) continue;
    // Skip if already inside a counter (defensive against re-processing).
    if (node.parentElement && node.parentElement.closest('ct-number-counter')) continue;

    const parts = text.split(NUMBER_RE_G);
    if (parts.length <= 1) continue;

    const frag = document.createDocumentFragment();
    for (const part of parts) {
      if (part === '') continue;
      if (new RegExp('^' + NUMBER_RE.source + '$').test(part)) {
        const counter = document.createElement('ct-number-counter');
        counter.dataset.endValue = part.replace(/,/g, '');
        counter.dataset.format = part;

        // Invisible sizer: holds the final value, reserves width + baseline,
        // and is the value read by screen readers.
        const sizer = document.createElement('span');
        sizer.className = 'ct-counter__sizer';
        sizer.textContent = part;

        // Animating digits overlay the sizer; hidden from assistive tech.
        const value = document.createElement('span');
        value.className = 'ct-counter__value';
        value.setAttribute('aria-hidden', 'true');
        value.textContent = part;

        counter.append(sizer, value);
        frag.appendChild(counter);
      } else {
        frag.appendChild(document.createTextNode(part));
      }
    }
    node.parentNode.replaceChild(frag, node);
  }
}

function initCtCounters() {
  for (const el of document.querySelectorAll('.ct-animate-numbers')) {
    if (el.dataset.ctProcessed) continue;
    el.dataset.ctProcessed = 'true';
    replaceNumbersInElement(el);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCtCounters);
} else {
  initCtCounters();
}

// Re-scan when a section is re-rendered in the theme editor.
document.addEventListener('shopify:section:load', initCtCounters);
